<template lang="pug">
  figure.audio-player.px-10.md-px-0.md-w-1x2.mx-auto.mt-120.mb-110.md-mt-217.md-mb-120.select-none
    transition(name="quickfade")
      //- square bounding box
      .relative.pb-ar-sqr.trans-quicker(:class="{'opacity-50': loading}")
        //- (has audio)
        template(v-if="row.audio_player")
          //- current time (half-circle)
          .absolute.inset-0
            //- (time)
            .absolute.h-full.flex.items-center.text-13.px-12.translx_-100(v-if="winIs('md')", :style="{left: `calc(${tX + 0.5} * 100%)`}") {{hms(time)}}
            //- half-circle
            .absolute.inset-0.border.rounded-full.border-app-text.overflow-hidden
              arc.block.absolute(:dX="tX", :style="{'-webkit-clip-path': tX < 0 ? 'inset(0 50% 0 0)' : 'inset(0 0 0 50%)', top: '-1px', left: '-1px', height: 'calc(100% + 2px)', width: 'calc(100% + 2px)'}")
          //- (hover/seek cursor)
          .absolute.inset-0.border.rounded-full.border-app-text(v-show="hoverLineVisible", v-if="canSeek")
            //- time
            .absolute.h-full.flex.items-center.text-13.text-app-accent.px-12(:style="{left: `calc(${mX} * 100%)`}") {{hms(duration * mX)}}
            //- half-circle
            arc.block.absolute.text-app-accent(:dX="hX", :style="{'-webkit-clip-path': hX < 0 ? 'inset(0 50% 0 0)' : 'inset(0 0 0 50%)', top: '-1px', left: '-1px', height: 'calc(100% + 2px)', width: 'calc(100% + 2px)'}")
          //- content
          .absolute.inset-0
            //- (mobile time/duration)
            .md-hidden.absolute.top-0.left-0.w-full.text-center.py-15.pointer-events-none.text-11(v-show="duration > 1")
              span(v-if="time") {{hms(time)}}
              span(v-else) {{hms(duration)}}
            //- audio file
            audio(ref="audio", :src="row.audio_player.url", @loadedmetadata="onLoadedMetaData", @pause="paused = true", @playing="onPlaying", @timeupdate="onTime", @error="onError", @stalled="onStalled", @waiting="onWaiting")
            //- interactive layer
            .absolute.z-10.w-full.inset-0.overflow-hidden.flex.items-center.justify-center
              //- seek on hover
              .absolute.inset-0.cursor-pointer(v-if="canSeek", v-show="!paused", @mousemove="onMousemove", @touchmove="onMousemove", @touchstart="hoverLineVisible = true", @touchend="onTouchEnd", @mouseenter="hoverLineVisible = true", @mouseleave="hoverLineVisible = false", @click="onSeekClick")
              //- play/pause btn
              button.relative.z-10.p-20.hover-scale-150.text-app-text.hover-text-app-text.trans-quicker(aria-label="paused ? 'Play' : 'Pause'", @click="togglePlay", :disabled="loading")
                play-icon.w-20.h-20.md-w-24.md-h-24.pointer-events-none(v-show="paused")
                pause-icon.w-20.h-20.md-w-24.md-h-24.pointer-events-none(v-show="!paused")
            //- duration (right edge)
            span.hidden.md-flex.absolute.top-0.right-0.h-full.items-center.justify-center.px-12.translx-100.text-13(v-if="duration") {{hms(duration)}}
            //- title
            figcaption.absolute.bottom-0.left-0.w-full.h-1x2.flex.items-center.justify-center
              .block.mx-auto.w-7x12.text-center.text-15.md-text-24
                h5 {{ row.audio_player.title }}
                h6.italic(v-if="row.audio_player.caption") {{ row.audio_player.caption }}

        //- (missing audio)
        template(v-else)
          .absolute.inset-0.flex.items-center.justify-center.text-13.leading-none.rounded-full.border.border-dashed.opacity-50 Audio Missing
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Container from '@/components/Container'
import PlayIcon from '@/components/Icons/PlayIcon'
import PauseIcon from '@/components/Icons/PauseIcon'
import Arc from '@/components/Icons/AudioArc'
export default {
  name: 'AudioPlayer',
  props: ['row', 'index'],
  data () {
    return {
      loading: true,
      paused: true,
      time: 0,
      duration: 1,
      mX: 0, // mouseX
      hoverLineVisible: false
    }
  },
  computed: {
    ...mapState(['canHover']),
    ...mapGetters(['winIs']),
    tX () {
      // ticker X pos
      return this.time / this.duration - 0.5
    },
    hX () {
      // hover arc X value (for svg x-radius)
      return this.mX - 0.5
    },
    savedId () {
      return `audio_${this.row.audio_player.id}_time`
    },
    canSeek () {
      return true // this.canHover
    }
  },
  methods: {
    onLoadedMetaData (e) {
      this.loading = false
      this.duration = this.$refs.audio.duration
      window.addEventListener('unload', this.savePosition)
      // jump to saved position ?
      const savedPos = window.localStorage.getItem(this.savedId)
      if (savedPos && this.canSeek) this.$refs.audio.currentTime = this.time = savedPos
    },
    togglePlay () {
      if (this.$refs.audio.paused) return this.$refs.audio.play()
      return this.$refs.audio.pause()
    },
    onTime () {
      this.time = this.$refs.audio && this.$refs.audio.currentTime
    },
    hms (d) {
      // display seconds as hours:mins:secs
      d = Number(d)
      const h = Math.floor(d / 3600)
      const m = Math.floor(d % 3600 / 60)
      const s = Math.floor(d % 3600 % 60)

      const hDisplay = h > 0 ? h + ':' : ''
      const mDisplay = m > 0 ? ('0' + m).slice(-2) : '0'
      const sDisplay = s > 0 ? ':' + ('0' + s).slice(-2) : ':00'
      return hDisplay + mDisplay + sDisplay
    },
    onMousemove (e) {
      const w = e.target.offsetWidth
      this.mX = e.layerX / w // - 0.5
    },
    onTouchEnd () {
      this.hoverLineVisible = false
      this.onSeekClick()
    },
    onSeekClick () {
      this.$refs.audio.currentTime = this.duration * this.mX
    },
    onPlaying (e) {
      console.log('AudioPlayer @playing', e)
      this.paused = this.loading = false
    },
    onError (e) {
      console.error('AudioPlayer @error', e)
    },
    onStalled (e) {
      console.warn('AudioPlayer @stalled', e)
    },
    onWaiting (e) {
      this.loading = true
      console.log('AudioPlayer @waiting', e)
    },
    savePosition () {
      // const isTouchAndShortAudio = !this.canSeek && this.duration < 5 * 60
      if (!this.$refs.audio || !this.canSeek) return
      window.localStorage.setItem(this.savedId, this.$refs.audio.currentTime)
    }
  },
  components: { Container, PlayIcon, PauseIcon, Arc }
}
</script>
